import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { MachineForm } from '@/components/machines/MachineForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { createMachineRequest } from '@/services/machines';
import type { CreateMachineBody, MachineDetail, UpdateMachineBody } from '@/types/machines';

export function MachineCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useMutation<MachineDetail, ApiError, { body: CreateMachineBody }>({
    mutationFn: ({ body }) => createMachineRequest(body),
    onSuccess: (created) => {
      toast.success(`Machine '${created.name}' created`);
      void queryClient.invalidateQueries({ queryKey: ['machines'] });
      void navigate(`/machines/${String(created.id)}`);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not create machine', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('machines.create')) {
    return <Navigate to="/dashboard" replace />;
  }

  function handleSubmit(body: CreateMachineBody | UpdateMachineBody): void {
    setErrorMessage(null);
    // create-mode body always has the Create shape (machineTypeId + siteId).
    if ('machineTypeId' in body) {
      createMutation.mutate({ body });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Machine</h1>
        <p className="text-muted-foreground">
          Add a new machine and (optionally) allocate it to a site at creation.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New machine</CardTitle>
          <CardDescription>
            Pick a machine type to load its custom-field schema. Setting an initial site opens the
            allocation row in the same transaction.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MachineForm
            mode="create"
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
